import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { APP_DEVELOPED_BY } from '@ferty9/constants';
import type { AdminTab } from '../types';
import { tabTitles } from '../utils';
import type { BusinessReportNavigationItem } from '../lib/api/business-reports';
import {moduleTab,type ModulePageItem} from '../modules/types';

type IconProps = { size?: number; strokeWidth?: number };

function HamburgerIcon() {
  return <span aria-hidden="true">☰</span>;
}

function ChevronDownIcon({ size = 16, strokeWidth = 2.5 }: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function DashboardIcon({ size = 18, strokeWidth = 2 }: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-layout-dashboard">
      <rect width="7" height="9" x="3" y="3" rx="1" />
      <rect width="7" height="5" x="14" y="3" rx="1" />
      <rect width="7" height="9" x="14" y="10" rx="1" />
      <rect width="7" height="5" x="3" y="14" rx="1" />
    </svg>
  );
}

function MessageIcon({ size = 18, strokeWidth = 2 }: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-message-square">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function ClipboardIcon({ size = 18, strokeWidth = 2 }: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-clipboard-list">
      <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="M9 12h6" />
      <path d="M9 16h6" />
    </svg>
  );
}

function ListChecksIcon({ size = 18, strokeWidth = 2 }: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-list-checks">
      <path d="m3 17 2 2 4-4" />
      <path d="m3 7 2 2 4-4" />
      <path d="M13 6h8" />
      <path d="M13 12h8" />
      <path d="M13 18h8" />
    </svg>
  );
}

function TicketIcon({ size = 18, strokeWidth = 2 }: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-ticket-percent">
      <path d="M2 9a3 3 0 0 0 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 0 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
      <path d="M9 9h.01" />
      <path d="M15 15h.01" />
      <path d="m15 9-6 6" />
    </svg>
  );
}

function ClipboardCheckIcon({ size = 18, strokeWidth = 2 }: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-clipboard-check">
      <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="m9 14 2 2 4-4" />
    </svg>
  );
}

function ChartColumnIcon({ size = 18, strokeWidth = 2 }: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-chart-column">
      <path d="M3 3v18h18" />
      <path d="M18 17V9" />
      <path d="M13 17V5" />
      <path d="M8 17v-3" />
    </svg>
  );
}

function ClinicalReportsIcon({ size = 18, strokeWidth = 2 }: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 12h4l2-5 4 10 2-5h6" />
      <path d="M19 3v4" />
      <path d="M17 5h4" />
      <path d="M4 21h16" />
    </svg>
  );
}

function NpsReportsIcon({ size = 18, strokeWidth = 2 }: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
      <path d="M8 10h.01" />
      <path d="M12 10h.01" />
      <path d="M16 10h.01" />
    </svg>
  );
}

function BusinessReportsIcon({ size = 18, strokeWidth = 2 }: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect width="18" height="14" x="3" y="7" rx="2" />
      <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M3 12h18" />
      <path d="M12 12v3" />
    </svg>
  );
}

function UsersIcon({ size = 18, strokeWidth = 2 }: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-users">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function LockIcon({ size = 18, strokeWidth = 2 }: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-lock">
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function HelpIcon({ size = 18, strokeWidth = 2 }: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-help-circle">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </svg>
  );
}

function SettingsIcon({ size = 18, strokeWidth = 2 }: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-settings">
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.38a2 2 0 0 0-.73-2.73l-.15-.09a2 2 0 0 1-1-1.74v-.51a2 2 0 0 1 1-1.72l.15-.1a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function AssetIcon({ size = 18, strokeWidth = 2 }: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </svg>
  );
}

function LogoutIcon({ size = 18, strokeWidth = 2.5 }: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-log-out">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

function SunIcon({ size = 16, strokeWidth = 2.5 }: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-sun">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </svg>
  );
}

function MoonIcon({ size = 16, strokeWidth = 2.5 }: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-moon">
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </svg>
  );
}

type ThemeMode = 'light' | 'dark';
type SidebarGroupKey = string;
type SidebarNavItem = { tab: AdminTab; label: string; icon: ReactNode; subsection?: string; pending?: boolean };
type SidebarNavGroup = { key: SidebarGroupKey; title: string; icon?: ReactNode; items: SidebarNavItem[]; adminOnly?: boolean; emptyLabel?: string };
type SidebarFlyoutState = { key: SidebarGroupKey; top: number; left: number; width: number };

const BUSINESS_REPORT_TITLE_STACKS: Partial<Record<AdminTab, { section: string; title: string; description: string }>> = {
  'module-management': { section: 'Administration', title: 'Module Management', description: 'Review trusted modules, control access and follow every approved change.' },
  'clinical-dashboard': { section: 'Dashboard', title: 'Clinical Dashboard', description: 'Centre-scoped patient, treatment and clinical activity insights from governed source evidence.' },
  'business-dashboard': { section: 'Dashboard', title: 'Business Dashboard', description: 'Network-wide business performance and management insights.' },
  'doctor-report': { section: 'Clinical Reports', title: 'Doctor Report', description: 'Patient records, day-to-day clinical activity and doctor-wise performance in one centre-scoped report.' },
  'daily-clinical-report': { section: 'Clinical Reports', title: 'Daily Report', description: 'Patient activity, cycle performance and IVF insights across one shared centre-scoped clinical report.' },
  'doctor-kpi-report': { section: 'Clinical Reports', title: 'Doctor KPI Report', description: 'Current-month and financial-year-to-date doctor performance across twelve weighted clinical and financial KPIs.' },
  'doctor-ivf-performance': { section: 'Clinical Reports', title: 'Doctor IVF Performance', description: 'Centre- and doctor-wise IVF acquisition, conversion, procedures and financial performance.' },
  'fcdr-report': { section: 'Business Reports', title: 'FCDR Report', description: 'Follow-up monitoring, patient outcomes and centre-level conversion intelligence.' },
  'waterfall-report': { section: 'Business Reports', title: 'Waterfall', description: 'Monthly patient registration cohorts traced through qualifying OPU completion.' },
  'marketing-report': { section: 'Business Reports', title: 'Marketing Report', description: 'B2B, B2C and referral performance assembled from permitted operational report sources.' },
  'btl-report': { section: 'Business Reports', title: 'BTL Report', description: 'Centre-wise BTL and campaign registrations, advice, OPU and collections.' },
  'business-report-administration': { section: 'Business Reports', title: 'Report Administration', description: 'Manage the report catalogue, clinical centres and financial calendar.' },
  'business-report-centers': { section: 'Business Reports', title: 'Centre Master', description: 'Manage clinical centres, codes, launch dates, aliases, effective dates and access mappings.' },
  'business-report-financial-calendar': { section: 'Business Reports', title: 'Financial Calendar', description: 'Define the financial-year start and dynamically derive reporting quarters.' },
  'business-report-sources': { section: 'Business Reports', title: 'Data Sources', description: 'Configure, govern, validate and activate Business Reports data sources.' },
  'business-revenue-sources': { section: 'Business Reports', title: 'Revenue Sources', description: 'Define governed revenue streams and how each contributes to Business Reports.' },
  'business-revenue-source-definitions': { section: 'Business Reports', title: 'Source Onboarding', description: 'Validate, reconcile, approve and activate new revenue sources.' },
  'business-revenue-category-rules': { section: 'Business Reports', title: 'Revenue Category Rules', description: 'Map source categories to revenue fields and contribution rules.' },
  'business-governance-ownership': { section: 'Business Reports', title: 'Governance & Ownership', description: 'Manage owners, validators, retention policies and maker-checker responsibilities.' },
  'business-targets-workspace': { section: 'Business Reports', title: 'Targets', description: 'Manage governed metrics and monthly targets for permitted centres, domains and teams.' },
  'business-revenue-operations': { section: 'Business Reports', title: 'Revenue Operations', description: 'Review discrepancies, process imports and reconcile governed revenue.' },
  'business-report-governance': { section: 'Business Reports', title: 'Target Governance', description: 'Define approved target metrics and projected-landing override policies.' },
  'business-manual-revenue': { section: 'Business Reports', title: 'Manual Revenue', description: 'Create and approve controlled manual revenue adjustments with audit history.' },
  'business-monthly-targets': { section: 'Business Reports', title: 'Monthly Targets', description: 'Maintain centre, month, metric, business-domain and team-level targets.' },
  'business-file-imports': { section: 'Business Reports', title: 'CSV / Excel Imports', description: 'Upload, preview, validate and confirm governed source files.' },
  'business-reconciliation': { section: 'Business Reports', title: 'Reconciliation', description: 'Compare imported results with expected totals and manage discrepancies.' },
  'business-operational-monitoring': { section: 'Business Reports', title: 'Operational Monitoring', description: 'Monitor ingestion jobs, source health, freshness, failures and retries.' },
  'business-data-integrity': { section: 'Business Reports', title: 'Data Integrity & Retention', description: 'Manage retention, correction history, integrity controls and recovery evidence.' },
};

const SIDEBAR_GROUP_STORAGE_KEY = 'f9-admin-sidebar-groups:v1';

export function AdminShell({
  activeTab,
  isAdmin,
  visibleTabs,
  isOpen,
  isCollapsed,
  darkMode,
  greetingLabel,
  userName,
  roleLabel,
  roleColor,
  children,
  topBarActions,
  onTab,
  onLogout,
  onSidebarToggle,
  onThemeToggle,
  onThemeChange,
  onChangePassword,
  businessReports = [],
  modulePages = [],
  canViewBusinessReports = false,
  canManageBusinessReports = false,
  canImportBusinessReports = false,
  businessReportsLoading = false,
  activeTabTitle,
}: {
  activeTab: AdminTab;
  isAdmin: boolean;
  visibleTabs: AdminTab[];
  isOpen: boolean;
  isCollapsed: boolean;
  darkMode: boolean;
  greetingLabel: string;
  userName: string;
  roleLabel: string;
  roleColor?: string;
  children: ReactNode;
  topBarActions?: ReactNode;
  onTab: (tab: AdminTab) => void;
  onLogout: () => void;
  onSidebarToggle: () => void;
  onThemeToggle: () => void;
  onThemeChange?: (theme: ThemeMode) => void;
  onChangePassword?: () => void;
  businessReports?: BusinessReportNavigationItem[];
  modulePages?: ModulePageItem[];
  canViewBusinessReports?: boolean;
  canManageBusinessReports?: boolean;
  canImportBusinessReports?: boolean;
  businessReportsLoading?: boolean;
  activeTabTitle?: string;
}) {
  const mainContentRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const pressedKeys = new Set<string>();
    let animationFrame = 0;
    let activeContainer: HTMLElement | null = null;

    const getScrollableTarget = (event: KeyboardEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return mainContentRef.current;
      }

      if (
        target.closest('input, textarea, select, button, [contenteditable="true"], .choices, [role="listbox"], [role="dialog"]')
      ) {
        return null;
      }

      const sidebarNav = target.closest('.sidebar-nav');
      if (sidebarNav instanceof HTMLElement) {
        return sidebarNav;
      }

      return mainContentRef.current;
    };

    const stopContinuousScroll = () => {
      pressedKeys.clear();
      activeContainer = null;
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = 0;
      }
    };

    const scrollBy = (container: HTMLElement, delta: number) => {
      const nextTop = Math.max(0, Math.min(container.scrollHeight - container.clientHeight, container.scrollTop + delta));
      container.scrollTop = nextTop;
    };

    const runContinuousScroll = () => {
      if (!activeContainer || pressedKeys.size === 0) {
        animationFrame = 0;
        return;
      }

      const step = Math.max(16, Math.round(activeContainer.clientHeight * 0.035));
      if (pressedKeys.has('ArrowDown')) scrollBy(activeContainer, step);
      if (pressedKeys.has('ArrowUp')) scrollBy(activeContainer, -step);

      animationFrame = window.requestAnimationFrame(runContinuousScroll);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }

      const container = getScrollableTarget(event);
      if (!container || container.scrollHeight <= container.clientHeight) {
        return;
      }

      const smallStep = Math.max(48, Math.round(container.clientHeight * 0.12));
      const pageStep = Math.max(120, Math.round(container.clientHeight * 0.85));

      const scrollTo = (top: number) => {
        const nextTop = Math.max(0, Math.min(container.scrollHeight - container.clientHeight, top));
        container.scrollTop = nextTop;
      };

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        activeContainer = container;
        pressedKeys.add('ArrowDown');
        if (!event.repeat) scrollBy(container, smallStep);
        if (!animationFrame) animationFrame = window.requestAnimationFrame(runContinuousScroll);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        activeContainer = container;
        pressedKeys.add('ArrowUp');
        if (!event.repeat) scrollBy(container, -smallStep);
        if (!animationFrame) animationFrame = window.requestAnimationFrame(runContinuousScroll);
      } else if (event.key === 'PageDown') {
        event.preventDefault();
        stopContinuousScroll();
        scrollTo(container.scrollTop + pageStep);
      } else if (event.key === 'PageUp') {
        event.preventDefault();
        stopContinuousScroll();
        scrollTo(container.scrollTop - pageStep);
      } else if (event.key === 'Home') {
        event.preventDefault();
        stopContinuousScroll();
        scrollTo(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        stopContinuousScroll();
        scrollTo(container.scrollHeight);
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        pressedKeys.delete(event.key);
        if (pressedKeys.size === 0) {
          activeContainer = null;
        }
      }
    };

    const onBlur = () => stopContinuousScroll();

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      stopContinuousScroll();
    };
  }, []);

  return (
    <div className="app-layout">
      <div id="sidebar-overlay" className={isOpen ? 'active' : ''} style={{ display: isOpen ? 'block' : undefined }} onClick={onSidebarToggle} />
      <Sidebar activeTab={activeTab} isAdmin={isAdmin} visibleTabs={visibleTabs} isOpen={isOpen} isCollapsed={isCollapsed} darkMode={darkMode} greetingLabel={greetingLabel} userName={userName} roleLabel={roleLabel} roleColor={roleColor} onTab={onTab} onLogout={onLogout} onToggle={onSidebarToggle} onThemeChange={onThemeChange} onChangePassword={onChangePassword} businessReports={businessReports} modulePages={modulePages} canViewBusinessReports={canViewBusinessReports} canManageBusinessReports={canManageBusinessReports} canImportBusinessReports={canImportBusinessReports} businessReportsLoading={businessReportsLoading} />
      <div className="main-wrapper" aria-label={`${activeTabTitle || tabTitles[activeTab] || 'Business Report'} content`}>
        <main ref={mainContentRef}>
          <TopBar activeTab={activeTab} activeTabTitle={activeTabTitle} darkMode={darkMode} actions={topBarActions} onSidebarToggle={onSidebarToggle} onThemeToggle={onThemeToggle} />
          {children}
        </main>
      </div>
    </div>
  );
}

export function TopBar({ activeTab, activeTabTitle, darkMode, actions, onSidebarToggle, onThemeToggle }: { activeTab: AdminTab; activeTabTitle?: string; darkMode: boolean; actions?: ReactNode; onSidebarToggle: () => void; onThemeToggle: () => void }) {
  const titleStack = BUSINESS_REPORT_TITLE_STACKS[activeTab] || (activeTab === 'feedback-tracker'
    ? {
        section: 'Patient Engagement',
        title: 'Feedback Tracker',
        description: 'Feedback administration and management.',
      }
    : activeTab === 'business-report:DAILY_REVENUE_SNAPSHOT'
      ? {
          section: 'Business Reports',
          title: 'Daily Revenue Snapshot',
          description: 'Centre-wise revenue, targets and conversion measures.',
        }
    : activeTab === 'business-report:MEDICS_REVENUE'
      ? {
          section: 'Business Reports',
          title: 'Medics Revenue Report',
          description: 'Centre-wise MAIN and PHARMACY revenue from governed Medics imports.',
        }
    : activeTab === 'offer-campaigns'
      ? {
          section: 'Patient Engagement',
          title: 'Offer Campaigns',
          description: 'Enroll patients into active offer campaigns and verify payment.',
        }
    : activeTab === 'settings'
      ? {
          section: 'Administration',
          title: 'Integration Settings',
          description: 'Manage integrations, parameters, providers and keys settings',
        }
      : activeTab === 'general-settings'
        ? {
            section: 'Administration',
            title: 'General Settings',
            description: 'Manage app behavior, automation rules and workflow settings',
          }
      : null);
  return (
    <div className={`page-header${activeTab === 'module-management' ? ' module-management-page-header' : ''}`}>
      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <MobileSidebarToggle onClick={onSidebarToggle} />
        {titleStack ? (
          <div className="page-title-stack" id="page-title">
            <p>{titleStack.section}</p>
            <h2>{titleStack.title}</h2>
            <span>{titleStack.description}</span>
          </div>
        ) : (
          <h2 className="page-title-text" id="page-title">{activeTabTitle || tabTitles[activeTab] || 'Business Report'}</h2>
        )}
      </div>
      <div className="topbar-actions">
        {actions}
        <button id="theme-toggle" className="btn btn-outline" style={{ padding: '10px 18px', borderRadius: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, borderColor: 'var(--border)', boxShadow: 'var(--shadow-sm)' }} onClick={onThemeToggle}>
          <span id="theme-icon" style={{ display: 'inline-flex', alignItems: 'center' }}>{darkMode ? <SunIcon /> : <MoonIcon />}</span> <span id="theme-text">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
        </button>
      </div>
    </div>
  );
}

export function MobileSidebarToggle({ onClick }: { onClick: () => void }) {
  return <button id="mobile-sidebar-toggle" onClick={onClick} aria-label="Toggle sidebar"><HamburgerIcon /></button>;
}

export function Sidebar({ activeTab, isAdmin, visibleTabs, isOpen, isCollapsed, darkMode, greetingLabel, userName, roleLabel, roleColor, onTab, onLogout, onToggle, onThemeChange, onChangePassword, businessReports = [], modulePages = [], canViewBusinessReports = false, canManageBusinessReports = false, canImportBusinessReports = false, businessReportsLoading = false }: { activeTab: AdminTab; isAdmin: boolean; visibleTabs: AdminTab[]; isOpen: boolean; isCollapsed: boolean; darkMode: boolean; greetingLabel: string; userName: string; roleLabel: string; roleColor?: string; onTab: (tab: AdminTab) => void; onLogout: () => void; onToggle: () => void; onThemeChange?: (theme: ThemeMode) => void; onChangePassword?: () => void; businessReports?: BusinessReportNavigationItem[]; modulePages?: ModulePageItem[]; canViewBusinessReports?: boolean; canManageBusinessReports?: boolean; canImportBusinessReports?: boolean; businessReportsLoading?: boolean }) {
  const copyrightYear = new Date().getFullYear();
  const profileRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const flyoutRef = useRef<HTMLDivElement | null>(null);
  const flyoutTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const [flyout, setFlyout] = useState<SidebarFlyoutState | null>(null);
  const [desktopViewport, setDesktopViewport] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<SidebarGroupKey, boolean>>({
    dashboard: false,
    'clinical-reports': true,
    'nps-reports': true,
    'business-reports': true,
    'patient-engagement': true,
    administration: true,
  });
  const moduleGroups: SidebarNavGroup[] = [...new Map(modulePages.map(page => [page.moduleKey, page])).entries()].map(([moduleKey, representative]) => ({
    key: `module-${moduleKey}`,
    title: representative.moduleName,
    icon: moduleKey === 'assets-mgmt' ? <AssetIcon size={20} /> : moduleNavigationIcon(representative.icon),
    items: modulePages
      .filter(page => page.moduleKey === moduleKey)
      .sort((left, right) => left.order - right.order || left.label.localeCompare(right.label))
      .map(page => ({ tab: moduleTab(page), label: page.label, icon: moduleNavigationIcon(page.icon) })),
  }));
  const navGroups: SidebarNavGroup[] = [
    {
      key: 'dashboard',
      title: 'Dashboard',
      items: [
        { tab: 'dashboard', label: 'NPS Dashboard', icon: <DashboardIcon /> },
        { tab: 'clinical-dashboard', label: 'Clinical Dashboard', icon: <ChartColumnIcon /> },
        { tab: 'business-dashboard', label: 'Business Dashboard', icon: <ChartColumnIcon /> },
      ],
    },
    {
      key: 'clinical-reports',
      title: 'Clinical Reports',
      icon: <ClinicalReportsIcon size={20} />,
      items: [
        { tab: 'doctor-report', label: 'Doctor Report', icon: <ClipboardIcon /> },
        { tab: 'daily-clinical-report', label: 'Daily Report', icon: <ChartColumnIcon /> },
        { tab: 'doctor-kpi-report', label: 'Doctor KPI Report', icon: <ChartColumnIcon /> },
        { tab: 'doctor-ivf-performance', label: 'Doctor IVF Performance', icon: <ChartColumnIcon /> },
      ],
    },
    {
      key: 'nps-reports',
      title: 'NPS Reports',
      icon: <NpsReportsIcon size={20} />,
      items: [
        { tab: 'feedback', label: 'Feedback Responses', icon: <MessageIcon /> },
        { tab: 'question-wise', label: 'Question Responses', icon: <ClipboardCheckIcon /> },
        { tab: 'summary', label: 'Monthly Summary', icon: <ChartColumnIcon /> },
        { tab: 'detailed-summary', label: 'Detailed Summary', icon: <ClipboardIcon /> },
      ],
    },
    {
      key: 'business-reports',
      title: 'Business Reports',
      icon: <BusinessReportsIcon size={20} />,
      items: [
        ...(visibleTabs.includes('fcdr-report') ? [{ tab: 'fcdr-report' as AdminTab, label: 'FCDR Report', icon: <ChartColumnIcon /> }] : []),
        ...(visibleTabs.includes('waterfall-report') ? [{ tab: 'waterfall-report' as AdminTab, label: 'Waterfall', icon: <ChartColumnIcon /> }] : []),
        ...(visibleTabs.includes('marketing-report') ? [{ tab: 'marketing-report' as AdminTab, label: 'Marketing Report', icon: <ChartColumnIcon /> }] : []),
        ...(visibleTabs.includes('btl-report') ? [{ tab: 'btl-report' as AdminTab, label: 'BTL Report', icon: <ChartColumnIcon /> }] : []),
        ...(canManageBusinessReports ? [{ tab: 'business-report-administration' as AdminTab, label: 'Report Administration', icon: <ListChecksIcon /> }] : []),
        ...(canManageBusinessReports ? [{ tab: 'business-report-sources' as AdminTab, label: 'Data Sources', icon: <ListChecksIcon /> }] : []),
        ...(visibleTabs.includes('business-governance-ownership') ? [{ tab: 'business-governance-ownership' as AdminTab, label: 'Governance & Ownership', icon: <ClipboardCheckIcon /> }] : []),
        ...(visibleTabs.includes('business-targets-workspace') ? [{ tab: 'business-targets-workspace' as AdminTab, label: 'Targets', icon: <ChartColumnIcon /> }] : []),
        ...(visibleTabs.includes('business-revenue-operations') ? [{ tab: 'business-revenue-operations' as AdminTab, label: 'Revenue Operations', icon: <ClipboardCheckIcon /> }] : []),
        ...(visibleTabs.includes('business-operational-monitoring') ? [{ tab: 'business-operational-monitoring' as AdminTab, label: 'Operational Monitoring', icon: <ChartColumnIcon /> }] : []),
        ...(visibleTabs.includes('business-data-integrity') ? [{ tab: 'business-data-integrity' as AdminTab, label: 'Data Integrity & Retention', icon: <ClipboardCheckIcon /> }] : []),
        ...businessReports.map((report) => ({
          tab: `business-report:${report.key}` as AdminTab,
          label: report.key === 'DAILY_REVENUE_SNAPSHOT' ? 'Daily Revenue' : report.name,
          icon: <ChartColumnIcon />,
        })),
      ],
      emptyLabel: businessReportsLoading ? 'Loading reports...' : 'No active reports',
    },
    {
      key: 'patient-engagement',
      title: 'Patient Engagement',
      items: [
        { tab: 'offer-campaigns', label: 'Offer Campaigns', icon: <TicketIcon /> },
        { tab: 'feedback-tracker', label: 'Feedback Tracker', icon: <ListChecksIcon /> },
      ],
    },
    ...moduleGroups,
    {
      key: 'administration',
      title: 'Administration',
      adminOnly: true,
      items: [
        { tab: 'staff', label: 'Staff Management', icon: <UsersIcon /> },
        { tab: 'users', label: 'User Accounts', icon: <LockIcon /> },
        { tab: 'questions', label: 'Survey Questions', icon: <HelpIcon /> },
        { tab: 'roles', label: 'Role Management', icon: <LockIcon /> },
        { tab: 'general-settings', label: 'General Settings', icon: <SettingsIcon /> },
        { tab: 'settings', label: 'Integration Settings', icon: <SettingsIcon /> },
        { tab: 'module-management', label: 'Module Management', icon: <SettingsIcon /> },
      ],
    },
  ];
  const visibleGroups = navGroups
    .filter(group => !group.adminOnly || isAdmin)
    .filter(group => group.key !== 'business-reports' || canViewBusinessReports)
    .map(group => ({ ...group, items: group.items.filter(item => visibleTabs.includes(item.tab)) }))
    .filter(group => group.items.length > 0 || (group.key === 'business-reports' && canViewBusinessReports));
  const visibleGroupKeys = visibleGroups.map(group => group.key).join('|');
  const railCollapsed = isCollapsed && desktopViewport;
  const flyoutGroup = flyout ? visibleGroups.find(group => group.key === flyout.key) : undefined;

  useEffect(() => {
    const query = window.matchMedia('(min-width: 769px)');
    const updateViewport = () => setDesktopViewport(query.matches);
    updateViewport();
    query.addEventListener('change', updateViewport);
    return () => query.removeEventListener('change', updateViewport);
  }, []);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(SIDEBAR_GROUP_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const saved = Object.fromEntries(Object.entries(parsed).filter((entry): entry is [string, boolean] => typeof entry[1] === 'boolean'));
      setCollapsedGroups(current => ({ ...current, ...saved }));
    } catch {
      // Ignore invalid local storage and keep defaults.
    }
  }, []);

  useEffect(() => {
    const activeGroup = visibleGroups.find(group => group.items.some(item => item.tab === activeTab));
    if (!activeGroup) return;
    setCollapsedGroups(current => {
      const next = { ...current };
      for (const group of visibleGroups) {
        next[group.key] = group.key !== activeGroup.key;
      }
      const changed = visibleGroups.some(group => current[group.key] !== next[group.key]);
      if (!changed) return current;
      persistCollapsedGroups(next);
      return next;
    });
  }, [activeTab, visibleGroupKeys]);

  const toggleGroup = (key: SidebarGroupKey) => {
    setCollapsedGroups(current => {
      const next = { ...current, [key]: !current[key] };
      persistCollapsedGroups(next);
      return next;
    });
  };

  const openSidebarGroup = (key: SidebarGroupKey, trigger: HTMLButtonElement) => {
    if (!railCollapsed) {
      setFlyout(null);
      toggleGroup(key);
      return;
    }

    const group = visibleGroups.find(candidate => candidate.key === key);
    if (!group) return;

    const rect = trigger.getBoundingClientRect();
    const sidebarRect = trigger.closest('.sidebar')?.getBoundingClientRect();
    const bodyZoom = Number.parseFloat(window.getComputedStyle(document.body).zoom);
    const coordinateScale = Number.isFinite(bodyZoom) && bodyZoom > 0 ? bodyZoom : 1;
    const viewportPadding = 12;
    const gap = 12;
    const visualViewportPadding = viewportPadding * coordinateScale;
    const visualGap = gap * coordinateScale;
    const anchorRight = sidebarRect?.right ?? rect.right;
    const availableWidth = (window.innerWidth - anchorRight - visualGap - visualViewportPadding) / coordinateScale;
    const width = Math.min(288, Math.max(220, availableWidth));
    const subsectionCount = new Set(group.items.map(item => item.subsection).filter(Boolean)).size;
    const estimatedHeight = Math.min(
      (window.innerHeight - (visualViewportPadding * 2)) / coordinateScale,
      78 + (group.items.length * 48) + (subsectionCount * 24),
    );
    const visualTop = Math.min(
      Math.max(visualViewportPadding, rect.top - (8 * coordinateScale)),
      Math.max(visualViewportPadding, window.innerHeight - (estimatedHeight * coordinateScale) - visualViewportPadding),
    );
    const visualLeft = Math.max(
      visualViewportPadding,
      Math.min(anchorRight + visualGap, window.innerWidth - (width * coordinateScale) - visualViewportPadding),
    );

    flyoutTriggerRef.current = trigger;
    setMenuOpen(false);
    setFlyout(current => current?.key === key ? null : {
      key,
      top: visualTop / coordinateScale,
      left: visualLeft / coordinateScale,
      width,
    });
  };

  useEffect(() => {
    if (railCollapsed) return;
    setFlyout(null);
    flyoutTriggerRef.current = null;
  }, [railCollapsed]);

  useEffect(() => {
    if (!flyout) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const activeDestination = flyoutRef.current?.querySelector<HTMLButtonElement>('[aria-current="page"]');
      const firstDestination = flyoutRef.current?.querySelector<HTMLButtonElement>('.sidebar-nav-flyout-item');
      (activeDestination || firstDestination)?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [flyout?.key]);

  useEffect(() => {
    if (!flyout) return undefined;
    const close = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && flyoutTriggerRef.current?.contains(target)) return;
      if (target instanceof Node && flyoutRef.current?.contains(target)) return;
      setFlyout(null);
    };
    const closeOnViewportChange = () => setFlyout(null);
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const trigger = flyoutTriggerRef.current;
      setFlyout(null);
      window.requestAnimationFrame(() => trigger?.focus({ preventScroll: true }));
    };
    window.addEventListener('mousedown', close);
    window.addEventListener('resize', closeOnViewportChange);
    window.addEventListener('scroll', closeOnViewportChange, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('resize', closeOnViewportChange);
      window.removeEventListener('scroll', closeOnViewportChange, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [flyout]);

  useEffect(() => {
    if (!flyout) return;
    const groupStillVisible = visibleGroups.some(group => group.key === flyout.key);
    if (railCollapsed && groupStillVisible) return;
    setFlyout(null);
    flyoutTriggerRef.current = null;
  }, [flyout, railCollapsed, visibleGroups]);

  const openProfileMenu = () => {
    const rect = profileRef.current?.getBoundingClientRect();
    if (rect) {
      setMenuRect({
        top: Math.max(12, rect.top - 166),
        left: Math.min(window.innerWidth - 236, Math.max(12, rect.left)),
        width: Math.max(220, rect.width + 54),
      });
    }
    setFlyout(null);
    setMenuOpen(open => !open);
  };

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && profileRef.current?.contains(target)) return;
      if (target instanceof Node && menuRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    const closeOnResize = () => setMenuOpen(false);
    window.addEventListener('mousedown', close);
    window.addEventListener('resize', closeOnResize, { once: true });
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('resize', closeOnResize);
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const chooseTheme = (theme: ThemeMode) => {
    onThemeChange?.(theme);
    setMenuOpen(false);
  };

  return (
    <aside className={`sidebar ${isOpen ? 'open' : ''} ${railCollapsed ? 'collapsed' : ''}`} id="sidebar">
      <div className="sidebar-header">
        <div className="brand"><img src="/logo.png" alt="Ferty9" /><span className="brand-text">{greetingLabel}</span></div>
        <button id="sidebar-toggle" onClick={onToggle} aria-label="Toggle sidebar"><HamburgerIcon /></button>
      </div>
      <nav className="sidebar-nav" aria-label="Admin navigation">
        {visibleGroups.map(group => (
          <SidebarNavSection
            key={group.key}
            group={group}
            collapsed={collapsedGroups[group.key] ?? group.key !== 'dashboard'}
            sidebarCollapsed={railCollapsed}
            flyoutOpen={flyout?.key === group.key}
            activeTab={activeTab}
            onToggle={trigger => openSidebarGroup(group.key, trigger)}
            onTab={onTab}
          />
        ))}
      </nav>
      <div className="sidebar-footer">
        <div className="sidebar-footer-main">
          <button ref={profileRef} type="button" className="user-profile-small sidebar-profile-trigger" aria-haspopup="menu" aria-expanded={menuOpen} onClick={openProfileMenu}>
            <div className="user-avatar" id="user-avatar-initial">{(userName[0] || 'A').toUpperCase()}</div>
            <div className="user-info-text">
              <div id="user-display-name" style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{userName}</div>
              <div id="user-display-center" style={{ fontSize: 11, color: roleColor || 'var(--primary)', fontWeight: 800, textTransform: 'uppercase' }}>{roleLabel}</div>
            </div>
          </button>
        <button className="sidebar-logout-btn" onClick={onLogout} onMouseEnter={event => { event.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'; }} onMouseLeave={event => { event.currentTarget.style.background = 'none'; }} title="Logout" aria-label="Logout">
          <LogoutIcon />
        </button>
        </div>
        <div className="sidebar-footer-credit">Developed By {APP_DEVELOPED_BY.replace('Department', 'Team')}</div>
        <div className="sidebar-legal-notice">
          &copy; {copyrightYear} Ferty9. All rights reserved.
        </div>
      </div>
      {menuOpen && menuRect && typeof document !== 'undefined' && createPortal(
        <div
          ref={menuRef}
          className="sidebar-profile-menu"
          role="menu"
          style={{
            position: 'fixed',
            top: menuRect.top,
            left: menuRect.left,
            width: menuRect.width,
            zIndex: 100000,
            background: 'var(--surface-solid)',
            border: '1px solid var(--border)',
            borderRadius: 16,
            boxShadow: 'var(--shadow-lg)',
            padding: 8,
          }}
        >
          <button type="button" role="menuitemradio" aria-checked={!darkMode} className="sidebar-profile-menu-item" onClick={() => chooseTheme('light')}>
            <span><SunIcon /></span>
            <span>Light Theme</span>
          </button>
          <button type="button" role="menuitemradio" aria-checked={darkMode} className="sidebar-profile-menu-item" onClick={() => chooseTheme('dark')}>
            <span><MoonIcon /></span>
            <span>Dark Theme</span>
          </button>
          <button type="button" role="menuitem" className="sidebar-profile-menu-item" onClick={() => { setMenuOpen(false); onChangePassword?.(); }}>
            <span><LockIcon size={16} /></span>
            <span>Change Password</span>
          </button>
          <button type="button" role="menuitem" className="sidebar-profile-menu-item danger" onClick={() => { setMenuOpen(false); onLogout(); }}>
            <span><LogoutIcon size={16} /></span>
            <span>Logout</span>
          </button>
        </div>,
        document.body,
      )}
      {railCollapsed && flyout && flyoutGroup && typeof document !== 'undefined' && createPortal(
        <SidebarNavFlyout
          activeTab={activeTab}
          containerRef={flyoutRef}
          group={flyoutGroup}
          position={flyout}
          onTab={tab => {
            setFlyout(null);
            onTab(tab);
          }}
        />,
        document.body,
      )}
    </aside>
  );
}

function persistCollapsedGroups(value: Record<SidebarGroupKey, boolean>) {
  try {
    window.localStorage.setItem(SIDEBAR_GROUP_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Local storage can be unavailable in privacy modes; navigation remains usable.
  }
}

function SidebarNavSection({ group, collapsed, sidebarCollapsed, flyoutOpen, activeTab, onToggle, onTab }: { group: SidebarNavGroup; collapsed: boolean; sidebarCollapsed: boolean; flyoutOpen: boolean; activeTab: AdminTab; onToggle: (trigger: HTMLButtonElement) => void; onTab: (tab: AdminTab) => void }) {
  let lastSubsection: SidebarNavItem['subsection'];
  const containsActiveTab = group.items.some(item => item.tab === activeTab);
  const flyoutId = `sidebar-${group.key}-flyout`;
  return (
    <div className={`nav-section ${collapsed ? 'collapsed' : ''} ${containsActiveTab ? 'has-active' : ''} ${flyoutOpen ? 'flyout-open' : ''}`} data-sidebar-group={group.key}>
      <button
        type="button"
        className="nav-section-toggle"
        onClick={event => onToggle(event.currentTarget)}
        aria-controls={sidebarCollapsed ? flyoutId : undefined}
        aria-expanded={sidebarCollapsed ? flyoutOpen : !collapsed}
        aria-haspopup={sidebarCollapsed ? 'dialog' : undefined}
        aria-label={sidebarCollapsed ? `Open ${group.title} navigation menu` : undefined}
        title={sidebarCollapsed ? group.title : undefined}
      >
        <span className="nav-section-heading">
          <span className="nav-section-icon" aria-hidden="true">{group.icon ?? iconForNavigationGroup(group.key)}</span>
          <span className="nav-section-title">{group.title}</span>
        </span>
        <span className={`nav-section-chevron ${collapsed ? '' : 'expanded'}`} aria-hidden="true"><ChevronDownIcon /></span>
      </button>
      {!sidebarCollapsed && !collapsed && (
        <div className="nav-section-items">
          {group.items.length === 0 && group.emptyLabel ? <p className="nav-section-empty">{group.emptyLabel}</p> : null}
          {group.items.map(item => {
            const showSubsection = Boolean(item.subsection && item.subsection !== lastSubsection);
            lastSubsection = item.subsection;
            return (
              <div className="nav-item-row" key={item.tab}>
                {showSubsection && <p className="nav-subsection-title">{item.subsection}</p>}
                <NavButton active={activeTab === item.tab} label={item.label} icon={item.icon} pending={Boolean(item.pending)} onClick={() => onTab(item.tab)} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SidebarNavFlyout({ activeTab, containerRef, group, position, onTab }: { activeTab: AdminTab; containerRef: RefObject<HTMLDivElement | null>; group: SidebarNavGroup; position: SidebarFlyoutState; onTab: (tab: AdminTab) => void }) {
  let lastSubsection: SidebarNavItem['subsection'];
  const flyoutId = `sidebar-${group.key}-flyout`;
  const headingId = `${flyoutId}-heading`;
  return (
    <div ref={containerRef} id={flyoutId} className="sidebar-nav-flyout" role="dialog" aria-modal="false" aria-labelledby={headingId} style={{ position: 'fixed', top: position.top, left: position.left, width: position.width, zIndex: 100000 }}>
      <div className="sidebar-nav-flyout-header">
        <span className="sidebar-nav-flyout-section-icon" aria-hidden="true">{group.icon ?? iconForNavigationGroup(group.key)}</span>
        <span className="sidebar-nav-flyout-heading">
          <strong id={headingId}>{group.title}</strong>
          <small>{group.items.length} {group.items.length === 1 ? 'destination' : 'destinations'}</small>
        </span>
      </div>
      <div className="sidebar-nav-flyout-list">
        {group.items.map(item => {
          const showSubsection = Boolean(item.subsection && item.subsection !== lastSubsection);
          lastSubsection = item.subsection;
          const active = activeTab === item.tab;
          return (
            <div className="sidebar-nav-flyout-row" key={item.tab}>
              {showSubsection && <p className="sidebar-nav-flyout-subsection">{item.subsection}</p>}
              <button type="button" className={`sidebar-nav-flyout-item ${active ? 'active' : ''}`} aria-current={active ? 'page' : undefined} onClick={() => onTab(item.tab)}>
                <span className="sidebar-nav-flyout-item-icon" aria-hidden="true">{item.icon}</span>
                <span className="sidebar-nav-flyout-item-label">{item.label}</span>
                {item.pending && <span className="nav-status-badge">New</span>}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NavButton({ active, label, icon, pending, onClick }: { active: boolean; label: string; icon: ReactNode; pending: boolean; onClick: () => void }) {
  return <button className={`nav-btn ${active ? 'active' : ''}`} onClick={onClick} aria-current={active ? 'page' : undefined}><span className="nav-icon">{icon}</span><span className="nav-text">{label}</span>{pending && <span className="nav-status-badge">New</span>}</button>;
}

function iconForNavigationGroup(group: SidebarGroupKey): ReactNode {
  if (group === 'dashboard') return <DashboardIcon size={20} />;
  if (group === 'clinical-reports' || group === 'nps-reports' || group === 'business-reports') return <ChartColumnIcon size={20} />;
  if (group === 'patient-engagement') return <TicketIcon size={20} />;
  return <UsersIcon size={20} />;
}

function moduleNavigationIcon(icon: string): ReactNode {
  if (icon === 'chart') return <ChartColumnIcon size={20} />;
  if (icon === 'check') return <ClipboardCheckIcon size={20} />;
  if (icon === 'settings') return <SettingsIcon size={20} />;
  return <DashboardIcon size={20} />;
}
